'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import { signInSchema } from '@eliadmin/core/src/components/auth/sign-in-schema';
import { applyServerErrors } from '@eliadmin/core/src/components/nt/account/shell/server-errors';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type Schema = ReturnType<typeof signInSchema>;
export type SignInFormValues = z.input<Schema>;

const FIELDS = ['email', 'password'] as const;

/**
 * State and submit of the sign-in form – the core flow of `sign-in-form.tsx`: `signIn`, the auth
 * cookie (`setAuthToken`), the customer store, the core „Boli ste prihlásený“ toast. Then
 * `redirectTo` (the page: a same-site `returnUrl` or „Môj profil“) or, with `null`, it stays on the
 * page (`StaySignInDialog`). A 422 goes onto the e-mail / password fields (the API names the e-mail
 * for wrong credentials), anything else is the general `error` next to the button.
 */
export function useSignInForm({ redirectTo }: { redirectTo: string | null }) {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [redirecting, startRedirect] = useTransition();
  const form = useForm<SignInFormValues, unknown, z.output<Schema>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    mode: 'onTouched',
    defaultValues: { email: '', password: '', 'g-recaptcha-response': '' },
  });

  const submit = form.handleSubmit(async values => {
    setError(null);
    try {
      const response = await signIn({ ...values, locale });
      await setAuthToken({ authToken: response.data.token });
      initCustomerStore({ authToken: response.data.token, customer: response.data.customer });
      toast({ title: t('text656'), variant: 'success' });
      if (redirectTo) startRedirect(() => router.replace(redirectTo));
    } catch (failure) {
      const rest = applyServerErrors(failure, form.setError, field =>
        FIELDS.find(name => name === field),
      );
      if (rest) setError(rest.join('\n') || t('text594'));
    }
  });

  return { form, submit, error, busy: form.formState.isSubmitting || redirecting };
}

export type SignInFormState = ReturnType<typeof useSignInForm>;
